import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FormFieldCaption,
  type FormFieldHelperDisplay,
} from '../FormFieldCaption/FormFieldCaption';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '../../ui/Select/Select';
import { cn } from '@hero/web-lib';

export type { FormFieldHelperDisplay as SelectAutoHelperDisplay };

export interface SelectAutoOption {
  value: string;
  /**
   * Label for the option. Can be:
   * - **string**: Simple text label. When combined with `icon`, automatically renders icon + text pattern.
   * - **ReactNode**: Custom content for advanced layouts (badges, multiple elements, custom styling, etc.)
   *
   * @example
   * // String label with icon (recommended for icon + text pattern)
   * { value: 'truck', label: 'Truck', icon: <TruckIcon /> }
   *
   * // String label without icon (simple text)
   * { value: 'option1', label: 'Option 1' }
   *
   * // ReactNode for custom layout (badges, multiple elements, etc.)
   * { value: 'premium', label: (
   *   <div className="flex items-center justify-between">
   *     <span>Premium Plan</span>
   *     <Badge>New</Badge>
   *   </div>
   * )}
   */
  label: string | React.ReactNode;
  /**
   * Optional icon to display before the label text.
   * Only applies automatic icon + text styling when `label` is a string.
   * If `label` is a ReactNode, the icon is ignored (include icon in your ReactNode instead).
   */
  icon?: React.ReactNode;
  /**
   * Optional description text to display below the label.
   * Supports both string (single language) or object with 'en' and 'ar' keys for bilingual support.
   */
  description?: string | { en: string; ar: string };
  disabled?: boolean;
}

export interface SelectAutoGroup {
  /** Group label */
  label: string;
  /** Options in this group */
  options: SelectAutoOption[];
}

export interface SelectAutoProps {
  /**
   * Array of options to display.
   * Can be either:
   * - Flat array of options: `SelectAutoOption[]`
   * - Grouped options: `SelectAutoGroup[]`
   */
  options: SelectAutoOption[] | SelectAutoGroup[];
  /** Current selected value */
  value?: string;
  /** Callback when value changes */
  onChange?: (value: string) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Optional label */
  label?: string;
  /** Error message */
  error?: string;
  /** Helper text — shown below the field or as a label-row info icon. */
  helperText?: string;
  /** `icon` — helper in a tooltip; `text` — helper paragraph below the select. */
  helperDisplay?: FormFieldHelperDisplay;
  /** Required field indicator */
  required?: boolean;
  /** Disabled state */
  disabled?: boolean;
  /** Optional className */
  className?: string;
  /** Optional className for trigger */
  triggerClassName?: string;
}

/**
 * SelectAuto - A complete, reusable select component that handles all UI internally.
 * Automatically handles RTL, styling, and provides a simple API.
 *
 * ### Flat Options (Default)
 * Simple array of options without grouping.
 *
 * ```tsx
 * <SelectAuto
 *   options={[
 *     { value: 'truck', label: 'Truck', icon: <TruckIcon /> },
 *     { value: 'van', label: 'Van', icon: <VanIcon /> },
 *   ]}
 *   value={selectedValue}
 *   onChange={setSelectedValue}
 * />
 * ```
 *
 * ### Grouped Options
 * Options organized into groups with labels.
 *
 * ```tsx
 * <SelectAuto
 *   options={[
 *     {
 *       label: 'Light Vehicles',
 *       options: [
 *         { value: 'van', label: 'Van', icon: <VanIcon /> },
 *         { value: 'car', label: 'Car', icon: <CarIcon /> },
 *       ],
 *     },
 *     {
 *       label: 'Heavy Vehicles',
 *       options: [
 *         { value: 'truck', label: 'Truck', icon: <TruckIcon /> },
 *         { value: 'trailer', label: 'Trailer', icon: <TrailerIcon /> },
 *       ],
 *     },
 *   ]}
 *   value={selectedValue}
 *   onChange={setSelectedValue}
 * />
 * ```
 *
 * ### String Label (Recommended for icon + text)
 * When `label` is a string and `icon` is provided, SelectAuto automatically renders
 * a consistent icon + text pattern with proper styling.
 *
 * ```tsx
 * <SelectAuto
 *   options={[
 *     { value: 'truck', label: 'Truck', icon: <TruckIcon /> },
 *     { value: 'van', label: 'Van', icon: <VanIcon /> },
 *   ]}
 *   value={selectedValue}
 *   onChange={setSelectedValue}
 * />
 * ```
 *
 * ### ReactNode Label (For custom layouts)
 * Use ReactNode when you need custom layouts, badges, multiple elements, or special styling.
 * Note: When using ReactNode, the `icon` prop is ignored - include the icon in your ReactNode.
 *
 * ```tsx
 * <SelectAuto
 *   options={[
 *     { value: 'premium', label: (
 *       <div className="flex items-center justify-between w-full">
 *         <span>Premium Plan</span>
 *         <Badge variant="success">New</Badge>
 *       </div>
 *     )},
 *     { value: 'basic', label: (
 *       <div className="flex items-center gap-2">
 *         <StarIcon />
 *         <span>Basic Plan</span>
 *         <span className="text-xs text-gray-500">Popular</span>
 *       </div>
 *     )},
 *   ]}
 *   value={selectedValue}
 *   onChange={setSelectedValue}
 * />
 * ```
 *
 * ### Simple String Label (No icon)
 * For simple text-only options without icons.
 *
 * ```tsx
 * <SelectAuto
 *   options={[
 *     { value: 'option1', label: 'Option 1' },
 *     { value: 'option2', label: 'Option 2' },
 *   ]}
 *   value={selectedValue}
 *   onChange={setSelectedValue}
 * />
 * ```
 */
export function SelectAuto({
  options,
  value,
  onChange,
  placeholder,
  label,
  error,
  helperText,
  helperDisplay = 'text',
  required = false,
  disabled = false,
  className,
  triggerClassName,
}: SelectAutoProps) {
  const { i18n } = useTranslation();
  const isRTL = ['ar', 'he'].includes(i18n.language);
  const openRef = useRef(false);

  // Check if options are grouped (check if first item has 'options' property)
  const isGrouped = options.length > 0 && typeof options[0] === 'object' && 'options' in options[0];

  // Flatten all options to find the selected one
  const allOptions: SelectAutoOption[] = isGrouped
    ? (options as SelectAutoGroup[]).flatMap(group => group.options)
    : (options as SelectAutoOption[]);

  const selectedOption = allOptions.find(option => option.value === value);

  // Helper function to get description text based on current language
  const getDescription = (
    description?: string | { en: string; ar: string },
  ): string | undefined => {
    if (!description) return undefined;
    if (typeof description === 'string') return description;
    return isRTL ? description.ar : description.en;
  };

  // Helper function to render option label (handles icon + text pattern)
  // Used for the selected value display (no description)
  const renderSelectedLabel = (option: SelectAutoOption) => {
    // If icon is provided and label is a string, render icon + text pattern
    if (option.icon && typeof option.label === 'string') {
      return (
        <div className="flex items-center gap-3">
          {option.icon}
          <span className="text-[16px] font-[600] leading-[24px] text-text">{option.label}</span>
        </div>
      );
    }
    // Otherwise, render the label as-is (could be ReactNode or string)
    return option.label;
  };

  // Helper function to render option label with description (for dropdown options)
  const renderOptionLabel = (option: SelectAutoOption) => {
    const description = getDescription(option.description);

    // If icon is provided and label is a string, render icon + text pattern
    if (option.icon && typeof option.label === 'string') {
      return (
        // `items-start` so the icon aligns with the label even
        // when the description wraps onto multiple lines.
        <div className="flex items-start gap-3">
          {option.icon}
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="text-[16px] font-[600] leading-[24px] text-text">
              {option.label}
            </span>
            {description && (
              <span className="text-[12px] font-[400] leading-[16px] text-gray-500 mt-0.5 whitespace-normal">
                {description}
              </span>
            )}
          </div>
        </div>
      );
    }

    // If description exists and label is a string (without icon), render label + description
    if (description && typeof option.label === 'string') {
      return (
        // `min-w-0` + `w-full` on the column lets the description
        // wrap inside whatever width the dropdown was pinned to
        // (see SelectContent below), instead of pushing the
        // popover wider than the trigger.
        <div className="flex w-full min-w-0 flex-col">
          <span className="text-[16px] font-[600] leading-[24px] text-text">
            {option.label}
          </span>
          <span className="text-[12px] font-[400] leading-[16px] text-gray-500 mt-0.5 whitespace-normal">
            {description}
          </span>
        </div>
      );
    }

    // Otherwise, render the label as-is (could be ReactNode or string)
    return option.label;
  };

  const selectControl = (
    <>
      <Select
        value={value ?? ''}
        dir={i18n.dir()}
        onOpenChange={open => {
          openRef.current = open;
        }}
        onValueChange={v => {
          if (openRef.current) onChange?.(v);
        }}
        disabled={disabled}>
        <SelectTrigger
          className={cn(
            'text-start w-full bg-white border border-gray-300 rounded-lg body1 px-3 py-2',
            selectedOption ? 'text-gray-900' : 'text-muted-foreground',
            error && 'border-destructive focus:border-destructive',
            triggerClassName,
          )}>
          {selectedOption ? (
            <SelectValue>{renderSelectedLabel(selectedOption)}</SelectValue>
          ) : (
            <SelectValue placeholder={placeholder} />
          )}
        </SelectTrigger>
        <SelectContent
          // Pin the dropdown to the trigger's width so long option
          // descriptions wrap inside the menu instead of pushing
          // the menu wider than the field. The Radix primitive
          // exposes `--radix-select-trigger-width` automatically;
          // we set both `w` and `max-w` so the dropdown matches
          // the field even when descriptions overflow.
          className="w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)]">
          {isGrouped
            ? (options as SelectAutoGroup[]).map((group, groupIndex) => (
                <React.Fragment key={group.label || `group-${groupIndex}`}>
                  {groupIndex > 0 && <SelectSeparator />}
                  <SelectGroup>
                    <SelectLabel>{group.label}</SelectLabel>
                    {group.options.map(option => (
                      <SelectItem
                        key={option.value}
                        value={option.value}
                        disabled={option.disabled}>
                        {renderOptionLabel(option)}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </React.Fragment>
              ))
            : (options as SelectAutoOption[]).map(option => (
                <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
                  {renderOptionLabel(option)}
                </SelectItem>
              ))}
        </SelectContent>
      </Select>
      {error ? <p className="text-xs font-medium text-destructive">{error}</p> : null}
    </>
  );

  if (label) {
    return (
      <FormFieldCaption
        label={label}
        helper={helperText}
        helperDisplay={helperDisplay}
        required={required}
        className={className}>
        {selectControl}
      </FormFieldCaption>
    );
  }

  return (
    <div className={cn('space-y-1', className)}>
      {selectControl}
      {helperText && !error && helperDisplay === 'text' ? (
        <p className="text-xs text-muted-foreground">{helperText}</p>
      ) : null}
    </div>
  );
}
